import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import { MISSION, MINDSET_INTRO, DISCUSSION_NORMS, TIMELINE, BRAND } from "../mock";

export default function About() {
  return (
    <div>
      {/* Header */}
      <section className="relative pt-16 sm:pt-24 pb-8 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-[0.28em] text-cyan-200/90">About</span>
            <span className="text-cyan-200/40">·</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-300/5 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-cyan-100">
              Launched September 2024
            </span>
          </div>
          <h1 className="font-serif-display text-[56px] md:text-[96px] font-bold leading-[0.98] text-white tracking-tight">
            Bridging <span className="hl-block-deep">campus</span>
            <br /> one conversation at a time.
          </h1>
        </div>
      </section>

      {/* Mission body */}
      <section className="relative py-12">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 space-y-8 text-slate-200 font-serif-body text-xl leading-relaxed">
          <p className="text-white text-2xl">{MISSION.short}</p>
          {MISSION.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-20 border-t border-white/10 bg-[#093349]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">Our History</p>
          <h2 className="font-serif-display text-5xl md:text-7xl font-semibold text-white leading-[1.02]">
            The <span className="hl-block-deep">timeline.</span>
          </h2>

          <div className="mt-14 relative">
            <div className="hidden md:block absolute left-3 top-2 bottom-2 w-px bg-white/15" />
            <div className="space-y-8">
              {TIMELINE.map((t, i) => (
                <article
                  key={i}
                  className="relative md:pl-12 rounded-2xl border border-white/15 bg-[#145074] p-7 md:p-9 hover:border-cyan-300/50 hover:bg-[#1a618d] transition-colors"
                >
                  <div className="hidden md:block absolute left-[-6px] top-10 w-3.5 h-3.5 rounded-full bg-cyan-300 shadow-[0_0_0_4px_rgba(9,51,73,1)]" />
                  <p className="text-xs uppercase tracking-[0.22em] text-cyan-200">{t.when}</p>
                  <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-white leading-tight">{t.title}</h3>
                  <p className="mt-4 text-slate-200 font-serif-body leading-relaxed max-w-3xl">{t.body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bridge Mindset & Norms */}
      <section id="norms" className="relative py-20 border-t border-white/10 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-6">The Bridge Mindset</p>
          <h2 className="font-serif-display text-5xl md:text-7xl font-semibold text-white leading-[1.02]">
            Norms of <span className="hl-block-deep">discussion.</span>
          </h2>

          <div className="mt-10 grid lg:grid-cols-12 gap-10">
            <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
              <div className="rounded-2xl border border-white/15 bg-[#145074] p-7">
                <p className="font-serif-body italic text-slate-100 text-lg leading-relaxed">
                  {MINDSET_INTRO}
                </p>
                <p className="mt-5 text-xs uppercase tracking-[0.2em] text-cyan-200/90">Every Bridge discussion is guided by moderators and these norms.</p>
              </div>
            </aside>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
              {DISCUSSION_NORMS.map((n, i) => (
                <div key={i} className="rounded-2xl border border-white/15 bg-[#145074] p-6 hover:border-cyan-300/50 hover:bg-[#1a618d] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center text-cyan-200 font-serif-display">
                      {i + 1}
                    </span>
                    <h3 className="font-serif-display text-xl text-white leading-snug">{n.title}</h3>
                  </div>
                  <p className="mt-4 text-slate-200 font-serif-body leading-relaxed">{n.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 flex flex-wrap gap-4">
            <Link to="/constitution" className="inline-flex items-center gap-2 rounded-full border border-cyan-300/70 hover:border-cyan-200 px-5 py-2.5 text-slate-100 hover:bg-cyan-300/10 transition-colors">
              <FileText className="w-4 h-4" /> Read the Constitution
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
